import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { mapConstants } from '@gisatcz/ptr-core';
import './style.scss';
import { jsx as _jsx } from "react/jsx-runtime";
var DEFAULT_MAX_WIDTH = 150;
var OPTIMAL_LAT = mapConstants.averageLatitude;

/**
 * @param view {Object} Panther map view
 * @param mapHeight {number} map component height in pixels
 * @param mapWidth {number} map component width in pixels
 * @param maxWidth {number} scale width in pixels
 * @returns {[number, string]} width of scale, label
 */
var getScaleOptions = function getScaleOptions(view, mapHeight, mapWidth, maxWidth) {
  var onePxInMeters = getAdjustedBoxRangeForCenter(view) / Math.min(mapHeight, mapWidth);
  var maxScaleWidthInMeters = maxWidth * onePxInMeters;
  var valueForScaleInMeters = getValueForScaleInMeters(maxScaleWidthInMeters);
  var scaleWidth = maxWidth * (valueForScaleInMeters / maxScaleWidthInMeters);
  var label = getFormattedLabel(valueForScaleInMeters);
  return [scaleWidth, label];
};
var MapScale = function MapScale(_ref) {
  var view = _ref.view,
    mapWidth = _ref.mapWidth,
    mapHeight = _ref.mapHeight,
    maxWidth = _ref.maxWidth,
    className = _ref.className;
  var maxScaleWidth = maxWidth || DEFAULT_MAX_WIDTH;
  if (view && mapWidth && mapHeight) {
    var classes = "ptr-MapScale ".concat(className);
    var _useMemo = useMemo(function () {
        return getScaleOptions(view, mapHeight, mapWidth, maxScaleWidth);
      }, [view, mapHeight, mapWidth, maxScaleWidth]),
      _useMemo2 = _slicedToArray(_useMemo, 2),
      scaleWidth = _useMemo2[0],
      label = _useMemo2[1];
    return /*#__PURE__*/_jsx("div", {
      className: classes,
      children: /*#__PURE__*/_jsx("div", {
        className: "ptr-MapScale-content",
        style: {
          width: scaleWidth + 'px'
        },
        children: /*#__PURE__*/_jsx("span", {
          children: label
        })
      })
    });
  } else {
    return null;
  }
};
MapScale.propTypes = {
  view: PropTypes.object,
  mapWidth: PropTypes.number,
  mapHeight: PropTypes.number,
  maxWidth: PropTypes.number,
  className: PropTypes.string
};

/**
 * Format value for label
 * @param value {number}
 * @returns {string}
 */
function getFormattedLabel(value) {
  if (value < 1000) {
    return "".concat(value, " m");
  } else {
    return "".concat((value / 1000).toLocaleString(), " km");
  }
}

/**
 * Calculate value in meters suitable for display
 * @param maxScaleWidthInMeters {number}
 * @returns {number}
 */
function getValueForScaleInMeters(maxScaleWidthInMeters) {
  var orderOfMagnitude = getOrderOfMagnitude(maxScaleWidthInMeters);
  var coeff = Math.pow(10, orderOfMagnitude);
  var normalizedValue = maxScaleWidthInMeters / coeff;
  if (normalizedValue < 2) {
    return coeff;
  } else if (normalizedValue < 3) {
    return 2 * coeff;
  } else if (normalizedValue < 5) {
    return 3 * coeff;
  } else {
    return 5 * coeff;
  }
}

/**
 * Currently, boxRange is optimized for optimal latitude. This function returns boxRange adjusted for current map center latitude
 * @param view {Object}
 * @returns {number} adjusted box range in meters
 */
function getAdjustedBoxRangeForCenter(view) {
  var boxRange = view.boxRange,
    center = view.center;
  return boxRange * (Math.cos(Math.PI * center.lat / 180) / Math.cos(Math.PI * OPTIMAL_LAT / 180));
}

/**
 * https://en.wikipedia.org/wiki/Order_of_magnitude
 * @param number {number}
 * @returns {number} Number of magnitude
 */
function getOrderOfMagnitude(number) {
  return Math.floor(Math.log(number) / Math.LN10 + 0.000000001);
}
export default MapScale;